import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Box, Skeleton, Tooltip } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import BrandIndexBreakups from "../../../Component/BrandIndexHistory/BrandIndexBreakupsNewV2";
import BrandIndexBreakupsNewV3 from "../../../Component/BrandIndexHistory/BrandIndexBreakupsNewV3";
import DirectIndexScoreCard from "../../../Component/DirectIndexScoreCard/DirectIndexScoreCard";
import CompositeIndexScoreChart from "../../../Component/CompositeIndexScoreChart/CompositeIndexScoreChart";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import WeightedDonutChart from "../../../Component/WeightedDonutChart/WeightedDonutChart";
import Header from "../../../Component/Header/Header";
import Modal from "../../../Component/Modal/Modal";
import Alert from "../../../Component/UI/Alert/Alert";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { AuthContext } from "../../../context/auth-context";
import { extractDomain } from "../../../utils/helpers";
import Attributes from "../Attributes/Attributes";
import BrandInsights from "../BrandInsights/BrandInsights";
import { DiscoveryContext } from "../discovery-context";
import LLMIndicator from "../LLMIndicator/LLMIndicator";
import ProductVariant from "../ProductVariant";
import Share from "../Share/Share";
import DiscoveryReportingModal from "../Reporting/DiscoveryReportingModal";
import classes from "./BrandScore.module.css";
import BrandAvatar from "../../../Component/Avatar/Avatar"
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import {
  calcBrandIndexScore,
  calcMultiFactorIndexScore,
  calcMultiFactorCompositeScore,
  getBrandPillarsAvg,
  getBrandPillarsDetail,
  getProductDimensionsAvg,
  getPurchaseCriteriaAvg,
  getSentimentScoreByFactor,
  getOrderedAccessibilityFactors,
  getOrderedCompositeFactors,
  calcDirectIndexScoreForEntity,
  isAccessibilityIndexScoreType,
  isCompositeIndexScoreType,
  RBI_COLORS,
  RBI_WEIGHTS,
  getFactorColor,
} from "../../../utils/brandIndexCalc";

export default function BrandScore({ remountChild, onViewInlineOpen, discoveryMode = "full", isBaselineHtmlReportEnabled = false }) {
  const [showBrandIndexBreakups, setShowBrandIndexBreakups] = useState(false);
  const [showDirectIndexBreakups, setShowDirectIndexBreakups] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportingModal, setShowReportingModal] = useState(false);
  const { user } = useContext(AuthContext);
  const enableHTMLReport = user?.is_discovery_html_report_enabled;
  const discoveryCtx = useContext(DiscoveryContext);
  const isAccessibility = isAccessibilityIndexScoreType(discoveryCtx.customIndexScoreType);
  const isComposite = isCompositeIndexScoreType(discoveryCtx.customIndexScoreType);
  const isMultiFactorMode = isAccessibility || isComposite;
  const entity = discoveryCtx.entity;
  const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";
  const discoveryType = discoveryCtx.type || "brand";
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const selectedInput = {
    llm,
    market,
    category,
    brand: discoveryCtx.entity,
    product: null,
  }


  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);


  let brandScore = discoveryCtx?.brand_scores[0];
  let loading = true;
  let domain = "";
  let variants = [];
  let hasError = false;

  const transactionStatus = discoveryCtx.state.transactionStatus
  domain = extractDomain(
    discoveryCtx.competitors[0]?.domain ?? "example.com"
  );
  if (brandScore && brandScore.extras && brandScore.total) {
    brandScore = discoveryCtx.brand_scores[0];
    variants =
      discoveryCtx.competitors[0]?.variants?.filter((v) => v.active) || [];
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }
  if (brandScore && brandScore.error) {
    loading = false;
    hasError = true;
  }


  let brandScoreAdjustments = { changed: false };
  if (brandScore?.extras?.rank) {
    const [{ name }] = brandScore?.extras?.rank;
    if (
      name.toString().toLowerCase().trim().replaceAll(" ", "") !==
      discoveryCtx.entity.toString().toLowerCase().trim().replaceAll(" ", "")
    ) {
      brandScoreAdjustments.entities = [name, discoveryCtx.entity];
      brandScoreAdjustments.changed = true;
    }
  }

  const isProductType = discoveryType === "product";
  const directEntityRankRow = isComposite
    ? (brandScore?.extras?.rank?.find((r) => r.name?.toLowerCase().trim() === entity?.toLowerCase().trim())
      ?? brandScore?.extras?.rank?.[0])
    : null;
  const directScore = isComposite
    ? calcDirectIndexScoreForEntity({
        rankScoreByFactor: directEntityRankRow?.rankScoreByFactor,
        sentimentScoreByFactor: getSentimentScoreByFactor(entity, brandScore?.extras?.favorabilities),
        brandPillarsDetail: isProductType ? [] : getBrandPillarsDetail(entity, discoveryCtx.core_attributes),
      })
    : null;

  return (
    <div className={classes.wrapper}>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        <div className={classes.container}>
          <div className={classes.brand_container}>
            <Header
              title={
                <div className={classes.title_container}>
                  {/* {domain && (
                    <img
                      className={classes.logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )} */}
                  <BrandAvatar
                    domainName={domain}
                    brandName={entity}
                  />
                  <div className="pt-2">
                    <div className={classes.title}>
                      <span>{entity}</span>
                      {variants.length > 0 && (
                        <ProductVariant
                          variants={variants}
                          triggerId="basic-menu"
                          editable={false}
                        >
                          <span className={classes.variant_text}>
                            {variants.length} variants
                          </span>
                        </ProductVariant>
                      )}
                    </div>
                    {discoveryCtx?.category && (
                      <div className={classes.label_container}>
                        <span className={classes.title_label}>
                          {discoveryCtx?.category}
                        </span>
                        {discoveryCtx?.market ? (
                          <>
                            <span>&middot;</span>
                            <span className={classes.title_label}>
                              {discoveryCtx?.market == "GeneralMarket" ? "General Market" : discoveryCtx?.market}
                            </span>
                          </>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              }
              titleClassName={classes.title}
              className={classes.header}
              renderAction={() => {
                return (
                  <Box display="flex" alignItems="center" gap={0.5} mr={1}>
                    {enableHTMLReport && (discoveryMode !== "baseline" || isBaselineHtmlReportEnabled) && !discoveryCtx.loading && discoveryCtx.state.transactionStatus === "COMPLETED" && discoveryType !== "category" && (
                      <MUIIconButton
                        title="Reporting"
                        onClick={() => setShowReportingModal(true)}
                      >
                        <SummarizeOutlinedIcon color="primary" />
                      </MUIIconButton>
                    )}
                    {!discoveryCtx.loading && (
                      <MUIIconButton
                        title="Click to Share"
                        onClick={() => setShowShareModal(true)}
                      >
                        <ShareOutlinedIcon color="primary" />
                      </MUIIconButton>
                    )}
                  </Box>
                );
              }}
            />
            <div className={`${classes.print_only} print-only`}>
              <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
            </div>
            <BrandInsights entity={entity} category={category} />
            <div className={classes.brand_index_wrapper}>
              <div className={classes.brand_index}>
                <div className={classes.main} style={{ display: "block" }}>
                  {!isComposite && !loading && brandScore?.extras && !hasError && (
                    <MUIIconButton
                      data-html2canvas-ignore
                      className={classes.info}
                      onClick={() => setShowBrandIndexBreakups(true)}
                    >
                      <InfoOutlinedIcon />
                    </MUIIconButton>
                  )}
                  <div style={{ width: "100%", paddingRight: 40 }}>
                    {!isComposite && (
                      <div style={{ marginBottom: 8 }}>
                        <span className={classes.brand_label}>
                          <span className={classes.llm_brand}>
                            {isAccessibility
                              ? "Accessibility Index Score"
                              : discoveryType === "brand" ? ENTITY_LABELS.brandIndexScore : ENTITY_LABELS.productIndexScore}
                          </span>
                          <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
                        </span>
                      </div>
                    )}

                    {isComposite && !loading && !hasError && (
                      <DirectIndexScoreCard
                        directScore={directScore}
                        onInfoClick={() => setShowDirectIndexBreakups(true)}
                        size="large"
                      />
                    )}

                    {loading && !hasError && <Skeleton width="100%" height={350} />}

                    {!loading && brandScore?.total && !hasError && (() => {
                      const isProduct = discoveryType === "product";
                      const brandPillarsAvg     = isProduct
                        ? getProductDimensionsAvg(entity, discoveryCtx.taxonomies)
                        : getBrandPillarsAvg(entity, discoveryCtx.core_attributes);
                      const purchaseCriteriaAvg = getPurchaseCriteriaAvg(entity, discoveryCtx.category_dimensions);

                      const sentimentScoreByFactor = isMultiFactorMode
                        ? getSentimentScoreByFactor(entity, brandScore?.extras?.favorabilities)
                        : null;
                      const multiFactorFactors = isComposite
                        ? getOrderedCompositeFactors(sentimentScoreByFactor, brandScore?.extras?.productMappingAttribute)
                        : getOrderedAccessibilityFactors(sentimentScoreByFactor, brandScore?.extras?.productMappingAttribute);
                      const entityRankRow = brandScore?.extras?.rank?.find(
                        (r) => r.name?.toLowerCase().trim() === entity?.toLowerCase().trim()
                      ) ?? brandScore?.extras?.rank?.[0];

                      const multiFactorArgs = {
                        factors: multiFactorFactors,
                        getRankForFactor: (factor) => entityRankRow?.rankScoreByFactor?.[factor]?.score ?? null,
                        getSentimentForFactor: (factor) => sentimentScoreByFactor?.[factor]?.normalizedScore ?? null,
                        getPillarForFactor: (factor) =>
                          getBrandPillarsDetail(entity, discoveryCtx.core_attributes)
                            .find((a) => a.name === factor)?.score ?? null,
                      };
                      const multiFactor = isMultiFactorMode && multiFactorFactors.length
                        ? (isComposite ? calcMultiFactorCompositeScore(multiFactorArgs) : calcMultiFactorIndexScore(multiFactorArgs))
                        : null;

                      const { total: rbiTotal, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions } =
                        multiFactor
                          ? { total: multiFactor.total }
                          : calcBrandIndexScore({
                              rank: brandScore?.rank,
                              favorability: brandScore?.favorability,
                              brandPillarsAvg,
                              purchaseCriteriaAvg,
                            });

                      const metricLabel = (key) =>
                        key === "visibility" ? ENTITY_LABELS.rank
                          : key === "sentiment" ? "Sentiment"
                          : isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;

                      // Accessibility mode is grouped factor-first: one card/segment per
                      // factor, each carrying its own visibility/sentiment/brand-pillar
                      // sub-breakdown (rather than one card per metric).
                      const SCORE_CARDS = multiFactor
                        ? multiFactor.breakdown.map((f, i) => ({
                            key: f.factor,
                            label: f.factor,
                            weightPct: Math.round(f.perFactorMax),
                            raw: f.total.toFixed(1),
                            max: Math.round(f.perFactorMax),
                            color: getFactorColor(i),
                          }))
                        : [
                            { key: "visibility",       label: ENTITY_LABELS.rank,                             weight: RBI_WEIGHTS.visibility,       raw: brandScore?.rank,        max: 50, contribution: contributions.visibility },
                            { key: "sentiment",        label: "Sentiment",                                   weight: RBI_WEIGHTS.sentiment,        raw: brandScore?.favorability, max: 50, contribution: contributions.sentiment },
                            { key: "brandPillars",     label: isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar, weight: RBI_WEIGHTS.brandPillars, raw: brandPillarsAvg,          max: 10, contribution: contributions.brandPillars },
                            { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,                 weight: RBI_WEIGHTS.purchaseCriteria, raw: purchaseCriteriaAvg,      max: 10, contribution: contributions.purchaseCriteria },
                          ];

                      const donutSegments = multiFactor
                        ? multiFactor.breakdown.map((f, i) => ({
                            key: f.factor,
                            label: f.factor,
                            color: getFactorColor(i),
                            sliceValue: Math.round(f.perFactorMax),
                            displayValue: f.total.toFixed(1),
                            weightPct: Math.round(f.perFactorMax),
                            sub: [
                              { label: metricLabel("visibility"), raw: f.visRaw, norm: f.visNorm, max: Math.round(f.perFactorMax) },
                              { label: metricLabel("sentiment"), raw: f.sentRaw, norm: f.sentNorm, max: Math.round(f.perFactorMax) },
                              { label: metricLabel("brandPillars"), raw: f.pillarRaw, norm: f.pillarsNorm, max: Math.round(f.perFactorMax) },
                            ],
                          }))
                        : undefined;

                      if (isComposite && multiFactor) {
                        return (
                          <CompositeIndexScoreChart
                            multiFactor={multiFactor}
                            size="large"
                            onInfoClick={brandScore?.extras ? () => setShowBrandIndexBreakups(true) : undefined}
                          />
                        );
                      }

                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                          <div style={{ flex: "0 0 38%", minWidth: 0 }}>
                            <WeightedDonutChart
                              total={rbiTotal}
                              visNorm={visNorm}
                              sentNorm={sentNorm}
                              pillarsNorm={pillarsNorm}
                              purchaseNorm={purchaseNorm}
                              size="large"
                              showLegend={false}
                              segments={donutSegments}
                              {...(multiFactor ? { outOf: Math.round(multiFactor.perFactorMax * multiFactor.breakdown.length) } : {})}
                            />
                          </div>
                          <div style={{ flex: "0 0 calc(62% - 20px)", minWidth: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
                            {SCORE_CARDS.map((c) => {
                              const color = multiFactor ? c.color : RBI_COLORS[c.key];
                              const weightPct = multiFactor ? c.weightPct : Math.round(c.weight * 100);
                              const rawDisplay = multiFactor
                                ? c.raw
                                : c.raw != null ? (c.max === 10 ? Number(c.raw).toFixed(1) : c.raw) : "—";
                              return (
                                <div key={c.key} style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: `1.5px solid ${color}33`, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color, flexShrink: 0, display: "inline-block", marginRight: 6 }} />
                                    <Tooltip title={c.label} arrow>
                                      <span style={{ fontSize: 12, fontWeight: 600, color: "#2d3748", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                                    </Tooltip>
                                    <span style={{ fontSize: 11, color: "#8a94a6", fontWeight: 500, flexShrink: 0, marginLeft: 4 }}>{weightPct}%</span>
                                  </div>
                                  <div style={{ lineHeight: 1 }}>
                                    <span style={{ fontSize: 26, fontWeight: 700, color }}>{rawDisplay}</span>
                                    <span style={{ fontSize: 12, fontWeight: 400, color: "#8a94a6" }}>/{c.max}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {hasError && (
                    <div className={classes.error_view}>
                      <ErrorView
                        title={`Error Fetching ${discoveryType === "brand" ? ENTITY_LABELS.brandIndexScore : ENTITY_LABELS.productIndexScore}`}
                        message={"Request Failed"}
                      />
                    </div>
                  )}
                </div>
                {brandScoreAdjustments.changed && (
                  <Alert
                    title={`${ENTITY_LABELS.rank} Score Adjustment`}
                    message={
                      <span>
                        The {ENTITY_LABELS.rank} score has been calculated using the{" "}
                        {discoveryCtx.type}{" "}
                        <b>{brandScoreAdjustments.entities[0]}</b> rather than{" "}
                        <b>{brandScoreAdjustments.entities[1]}</b>
                      </span>
                    }
                  />
                )}
              </div>
              <div className={classes.summary}>
                <Attributes
                  entity={entity}
                  category={category}
                  market={market}
                />
              </div>
            </div>
          </div>
        </div>
        <Modal
          open={showBrandIndexBreakups}
          handleClose={() => setShowBrandIndexBreakups(false)}
          width={1200}
        >
          <BrandIndexBreakups
            data={brandScore?.extras}
            isOpen={showBrandIndexBreakups}
            type={discoveryCtx.type}
            setShowBrandIndexBreakups={setShowBrandIndexBreakups}
            remountChild={remountChild}
            selectedInput={selectedInput}
            fromDiscoveryV2={true}
            compIndexScoreMetaData={discoveryCtx.brand_scores}
            brandProduct={entity}
          />
        </Modal>
        {isComposite && (
          <Modal
            open={showDirectIndexBreakups}
            handleClose={() => setShowDirectIndexBreakups(false)}
            width={1200}
          >
            <BrandIndexBreakupsNewV3
              data={brandScore?.extras}
              brandProduct={entity}
              selectedInput={selectedInput}
              fromDiscoveryV2={true}
              compIndexScoreMetaData={discoveryCtx.brand_scores}
            />
          </Modal>
        )}
        <Modal
          height="85vh"
          width="700"
          title="Share Link"
          open={showShareModal}
          handleClose={() => setShowShareModal(false)}
        >
          <Share setShowShareModal={setShowShareModal} />
        </Modal>
        <DiscoveryReportingModal
          open={showReportingModal}
          onClose={() => setShowReportingModal(false)}
          onViewInlineOpen={onViewInlineOpen}
        />
      </MUICard>
    </div>
  );
}
